import { useCallback } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { ValueReferenceCycle } from "@skilitics/nautilus-core-designer-data";

import { useGetNodeLink, type NodeLink } from "../../navigation/useGetNodeLink.ts";
import { useDescribeValueReferenceCycle } from "./describeValueReferenceCycle.ts";

export interface ValueReferenceCycleNodeSummary {
  title: string;
  /** Where to land the author, absent where following the name would land nowhere. */
  link?: NodeLink;
}

export interface ValueReferenceCycleSummary {
  key: string;
  /** The loop as the author reads it, closing back on the node it started from. */
  nodes: ValueReferenceCycleNodeSummary[];
  fixAdvice?: string;
}

/**
 * Flatten a cycle to titles, landing places and advice — everything needed to render it, with
 * nothing left to look up.
 *
 * The report of a blocked publish or preview shows in a toast, which renders outside the store and
 * the router, so it can't resolve any of this itself. Resolving it here also fixes what the author
 * is told to the design as it stood when the action was refused.
 */
export const useSummariseValueReferenceCycle = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const describeCycle = useDescribeValueReferenceCycle();
  const getNodeLink = useGetNodeLink();

  return useCallback(
    (cycle: ValueReferenceCycle): ValueReferenceCycleSummary => {
      const { hops, closesOn, breakable } = describeCycle(cycle);

      const summariseNode = ({ nodeId, nodeTitle }: { nodeId: number; nodeTitle: string }) => ({
        title: nodeTitle,
        link: getNodeLink(nodeId),
      });

      const [only, ...rest] = breakable;
      const fixAdvice = !only
        ? undefined
        : rest.length > 0
          ? formatMessage("fixAny")
          : only.valueTitle
            ? formatMessage("fixOne", { node: only.nodeTitle, value: only.valueTitle })
            : formatMessage("fixOneUnnamed", { node: only.nodeTitle });

      return {
        key: cycle.nodeIds.join(","),
        nodes: [...hops.map(summariseNode), summariseNode(closesOn)],
        fixAdvice,
      };
    },
    [describeCycle, formatMessage, getNodeLink],
  );
};

const formatStrings = {
  fixOne: {
    en: "Change or clear “{value}” on {node}.",
  },
  fixOneUnnamed: {
    en: "Change or clear the value on {node}.",
  },
  fixAny: {
    en: "Change or clear one of the values in the loop.",
  },
} satisfies FormatMessages;
